import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslationService } from '@spartacus/core';
import { Option } from '../../../core/models/option.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { InvoicesStatusRequest } from './core/invoice.model';
import { initialStatus, loadingStatus } from '../../../core/models/request-status.model';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { InvoiceService } from './core/invoice.service';
import { CustomValidatorService } from '../../../components/form-elements/custom-validator.service';
import { TableProConfig } from '../../../components/table-pro/core/table-pro.model';
import { ExcelDownloadService } from '../../../core/services/excelDownload.service';

@Component({
  selector: 'app-invoices',
  templateUrl: './invoices.component.html',
  styleUrl: './invoices.component.scss',
  standalone: false,
})
export class InvoicesComponent implements OnInit, OnDestroy {
  subscription = new Subscription();
  invoicesRequest$ = new BehaviorSubject<InvoicesStatusRequest>({
    ...initialStatus,
  });
  options$ = new BehaviorSubject<Option[]>([]);

  tableConfig: TableProConfig = {
    collumnConfig: [
      { id: 'invoiceNumber', index: 0, type: 'number' },
      { id: 'invoiceDate', index: 1, type: 'date' },
      { id: 'grossAmount', index: 2, type: 'text' },
      { id: 'amountOwed', index: 3, type: 'text' },
      { id: 'poNumber', index: 4, type: 'text' },
      { id: 'dueDate', index: 5, type: 'date' },
      { id: 'paymentTerm', index: 6, type: 'text' },
      { id: 'status', index: 7, type: 'text' },
      { id: 'creditAdj', index: 8, type: 'text' },
      { id: 'orderNumber', index: 9, type: 'number' },
    ],
  };

  minDate = new Date(new Date().setDate(new Date().getDate() - 365));
  maxDate = new Date();

  form: FormGroup = this.fb.group({
    searchBy: ['invoiceDate'],
    searchValue: [null],
    searchType: ['90'],
    fromDate: [new Date(new Date().setDate(new Date().getDate() - 90))],
    toDate: [this.maxDate],
    openOnly: [false, []],
    poNumber: [null],
    invoiceNumber: [null],
    orderNumber: [null],
    catalogNumber: [null],
    partNumber: [null],
    customerPartNumber: [null],
  });

  constructor(
    private invoiceService: InvoiceService,
    protected fb: FormBuilder,
    private customValidatorService: CustomValidatorService,
    private excelDownloadService: ExcelDownloadService,
    private translationService: TranslationService,
  ) {}

  ngOnInit(): void {
    this.listenToSearchBy();
    this.listenerSearchType();
    this.loadOptions();
    this.submit();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  backSearchType() {
    this.form.get('searchType')?.setValue('90');
  }

  listenerSearchType() {
    this.subscription.add(
      this.form.get('searchType')?.valueChanges.subscribe(value => {
        if (value === 'custom') {
          this.evaluateValidation(value as string);
        } else {
          this.setDaysToSearchByQuantity(value as string);
        }
      }),
    );
  }

  loadOptions(): void {
    this.subscription.add(
      combineLatest([
        this.translationService.translate('formSearch.dateRangeOptions.last30Days'),
        this.translationService.translate('formSearch.dateRangeOptions.last60Days'),
        this.translationService.translate('formSearch.dateRangeOptions.last90Days'),
        this.translationService.translate('formSearch.dateRangeOptions.customRange'),
      ]).subscribe(([last30Days, last60Days, last90Days, customRange]) => {
        this.options$.next([
          { code: '30', label: last30Days },
          { code: '60', label: last60Days },
          { code: '90', label: last90Days },
          { code: 'custom', label: customRange },
        ]);
      }),
    );
  }

  evaluateValidation(searchType: string) {
    const removeValidations = (control: string) => {
      this.form.get(control)?.clearValidators();
      this.form.get(control)?.updateValueAndValidity();
    };

    const addValidations = (control: string) => {
      this.form.get(control)?.setValidators([this.customValidatorService.required()]);
      this.form.get(control)?.updateValueAndValidity();
    };

    const fields = ['fromDate', 'toDate'];

    fields.forEach(field => removeValidations(field));

    if (searchType === 'custom') {
      fields.forEach(field => addValidations(field));
    }
  }

  setDaysToSearchByQuantity(days: string) {
    const qtyDays = parseInt(days);

    this.form.get('fromDate')?.setValue(new Date(new Date().setDate(new Date().getDate() - qtyDays)));
    this.form.get('toDate')?.setValue(this.maxDate);
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.getInvoices();
    }
  }

  onEnter(event: Event) {
    event.preventDefault();
    this.submit();
  }

  getInvoices(): void {
    const query = this.buildInvoiceQuery();
    this.invoicesRequest$.next({ ...loadingStatus });

    this.subscription.add(
      this.invoiceService.getInvoices(query).subscribe(response => {
        this.invoicesRequest$.next(response);
      }),
    );
  }

  exportInvoices(): void {
    const query = this.buildInvoiceQuery();

    this.subscription.add(
      this.invoiceService.exportInvoices(query).subscribe(response => {
        this.excelDownloadService.downloadBase64File(response);
      }),
    );
  }

  buildInvoiceQuery(): any {
    const { searchBy, searchValue, fromDate, toDate } = this.form.value;

    if (searchBy === 'invoiceDate') {
      const formattedFromDate = new Date(fromDate).toISOString().split('T')[0];
      const formattedToDate = new Date(toDate).toISOString().split('T')[0];
      return {
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        openOnly: this.form.get('openOnly')?.value || false,
      };
    } else {
      return {
        fromDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0],
        toDate: new Date().toISOString().split('T')[0],
        openOnly: this.form.get('openOnly')?.value || false,
        poNumber: searchBy === 'poNumber' ? searchValue : null,
        invoiceNumber: searchBy === 'invoiceNumber' ? searchValue : null,
        orderNumber: searchBy === 'orderNumber' ? searchValue : null,
        catalogNumber: searchBy === 'catalogNumber' ? searchValue : null,
        partNumber: searchBy === 'partNumber' ? searchValue : null,
        customerPartNumber: searchBy === 'customerPartNumber' ? searchValue : null,
      };
    }
  }

  resetFormFields(): void {
    const fields = ['poNumber', 'invoiceNumber', 'orderNumber', 'catalogNumber', 'partNumber', 'customerPartNumber'];
    fields.forEach(field => this.form.get(field)?.setValue(null));
    this.form.get('searchValue')?.setValue(null);
  }

  listenToSearchBy(): void {
    this.subscription.add(
      this.form.get('searchBy')?.valueChanges.subscribe(searchBy => {
        this.resetFormFields();
        if (searchBy === 'invoiceDate') {
          this.form.get('searchType')?.setValue('90');
          this.setDaysToSearchByQuantity('90');
        } else {
          this.form.get('fromDate')?.setValue(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
          this.form.get('toDate')?.setValue(this.maxDate);
          this.form.get('openOnly')?.setValue(false);
        }
      }),
    );
  }

  clearAll(): void {
    window.location.reload();
  }
}
